import React, { useEffect, useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { api } from "../api/alphaClient";
import { Trophy, TrendingUp, Layers, ArrowUpDown } from "lucide-react";

const PALETTE = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#f97316", "#a855f7"];
const REBASE_TO = 1000;

function pctCell(v) {
  if (v === null || v === undefined) return <span className="text-gray-500">—</span>;
  return (
    <span className={`font-semibold ${v >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
      {v >= 0 ? "+" : ""}{v.toFixed(2)}%
    </span>
  );
}

function cellBgColor(v) {
  if (v === null || v === undefined) return "bg-gray-900/40 text-gray-500";
  if (v >= 0) {
    if (v > 10) return "bg-emerald-500/20 text-emerald-300 font-semibold";
    return "bg-emerald-500/10 text-emerald-400";
  }
  if (v < -10) return "bg-rose-500/20 text-rose-300 font-semibold";
  return "bg-rose-500/10 text-rose-400";
}

function trailingChanges(series) {
  if (!series || series.length < 2) return { chg_1d: null, chg_1w: null, chg_4w: null };
  const navs = series.map((p) => p.nav);
  const last = navs[navs.length - 1];
  const back = (n) => {
    const ref = navs[navs.length - 1 - n];
    return navs.length > n && ref ? (last / ref - 1) * 100 : null;
  };
  return { chg_1d: back(1), chg_1w: back(5), chg_4w: back(20) };
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function monthLabel(monthKey) {
  const [y, m] = monthKey.split("-");
  return `${MONTH_LABELS[Number(m) - 1]} ${y}`;
}

function monthlyReturnsByKey(equityCurves) {
  const monthSet = new Set();
  const byKey = {};
  Object.entries(equityCurves || {}).forEach(([key, series]) => {
    if (!series || series.length === 0) return;
    const lastNavByMonth = {};
    const monthOrder = [];
    series.forEach((pt) => {
      const mk = pt.date.slice(0, 7);
      if (!(mk in lastNavByMonth)) monthOrder.push(mk);
      lastNavByMonth[mk] = pt.nav;
    });
    const returns = {};
    let prevNav = series[0].nav;
    monthOrder.forEach((mk) => {
      const nav = lastNavByMonth[mk];
      returns[mk] = prevNav ? (nav / prevNav - 1) * 100 : null;
      prevNav = nav;
      monthSet.add(mk);
    });
    byKey[key] = returns;
  });
  return { months: Array.from(monthSet).sort(), byKey };
}

function EquityTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const sorted = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0));

  return (
    <div className="bg-[#14171f] border border-gray-700 rounded-lg p-2.5 text-xs font-mono shadow-2xl w-[330px] select-none">
      <div className="font-bold text-gray-200 border-b border-gray-800 pb-1 mb-1.5 flex justify-between items-center text-[11px]">
        <span>{label}</span>
        <span className="text-[10px] text-gray-500 font-normal">Rebased NAV</span>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
        {sorted.map((entry) => (
          <div key={entry.dataKey} className="flex items-center justify-between gap-1 text-[10px]">
            <div className="flex items-center gap-1 truncate">
              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: entry.color }} />
              <span className={`truncate ${entry.dataKey === 'live_portfolio' ? 'text-cyan-300 font-bold' : 'text-gray-300'}`}>
                {entry.dataKey}
              </span>
            </div>
            <span className="font-bold text-gray-100 shrink-0 font-mono">
              {entry.value != null ? Number(entry.value).toFixed(1) : '—'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Overview({ universe }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedCurves, setSelectedCurves] = useState(null);
  const [sortKey, setSortKey] = useState("total_return_pct");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setSelectedCurves(null);
    api.getOverview(universe)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setStatus("ok");
        setSelectedCurves(new Set(Object.keys(res.equity_curves || {})));
      })
      .catch((err) => {
        if (!cancelled) { setStatus("error"); setErrorMsg(err.message || 'Failed to fetch overview data.'); }
      });
    return () => { cancelled = true; };
  }, [universe]);

  const { months, byKey: monthlyReturns } = useMemo(
    () => monthlyReturnsByKey(data?.equity_curves),
    [data?.equity_curves]
  );

  const boardWithChanges = useMemo(() => {
    if (!data?.leaderboard || !data?.equity_curves) return [];
    return data.leaderboard.map((r) => ({
      ...r,
      ...trailingChanges(data.equity_curves[r.key]),
    }));
  }, [data]);

  const sortedLeaderboard = useMemo(() => {
    const copy = [...boardWithChanges];
    copy.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return sortDir === "desc" ? bv - av : av - bv;
    });
    return copy;
  }, [boardWithChanges, sortKey, sortDir]);

  function toggleSort(key) {
    if (key === sortKey) setSortDir(sortDir === "desc" ? "asc" : "desc");
    else { setSortKey(key); setSortDir("desc"); }
  }

  const chartData = useMemo(() => {
    if (!data?.equity_curves || !selectedCurves) return [];
    const dateMap = {};
    selectedCurves.forEach((key) => {
      const series = data.equity_curves[key] || [];
      const firstNav = series.find((pt) => pt && pt.nav != null)?.nav;
      const scale = firstNav ? REBASE_TO / firstNav : 1;
      series.forEach((pt) => {
        if (!pt || !pt.date) return;
        dateMap[pt.date] = dateMap[pt.date] || { date: pt.date };
        dateMap[pt.date][key] = pt.nav != null ? Number((pt.nav * scale).toFixed(2)) : null;
      });
    });
    return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
  }, [data?.equity_curves, selectedCurves]);

  const activeCurveKeys = useMemo(() => {
    if (!data?.equity_curves || !selectedCurves) return [];
    return Object.keys(data.equity_curves).filter((k) => selectedCurves.has(k));
  }, [data?.equity_curves, selectedCurves]);

  const topStrategy = sortedLeaderboard[0];
  const avgReturn = sortedLeaderboard.length
    ? sortedLeaderboard.reduce((acc, r) => acc + (r.total_return_pct || 0), 0) / sortedLeaderboard.length
    : 0;

  if (status === "loading") {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-[#1a1d29] border border-gray-800 rounded-xl text-gray-400 font-mono">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-3"></div>
        <span>Loading Alpha Strategy Overview for {universe}...</span>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="bg-[#1a1d29] border border-rose-500/30 p-6 rounded-xl text-rose-400 font-mono text-center">
        <p className="font-bold mb-1">Error Loading Strategy Overview</p>
        <p className="text-xs text-rose-400/80">{errorMsg}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 h-full overflow-y-auto pr-1 relative">
      {/* Top KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 shrink-0 relative z-10">
        <div className="bg-[#1a1d29] border border-gray-800 p-4 rounded-xl flex items-center justify-between shadow-md">
          <div>
            <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Top Performing Strategy</div>
            <div className="text-base font-bold text-gray-100 font-mono mt-0.5">{topStrategy?.key || "N/A"}</div>
          </div>
          <div className="text-right font-mono">
            <div className="text-sm font-bold text-emerald-400">+{topStrategy?.total_return_pct?.toFixed(1)}%</div>
            <div className="text-[10px] text-gray-500">Return</div>
          </div>
        </div>

        <div className="bg-[#1a1d29] border border-gray-800 p-4 rounded-xl flex items-center justify-between shadow-md">
          <div>
            <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Universe Avg Return</div>
            <div className="text-base font-bold text-gray-100 font-mono mt-0.5">{avgReturn.toFixed(1)}%</div>
          </div>
          <TrendingUp className="w-6 h-6 text-blue-400 opacity-80" />
        </div>

        <div className="bg-[#1a1d29] border border-gray-800 p-4 rounded-xl flex items-center justify-between shadow-md">
          <div>
            <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Active Variants</div>
            <div className="text-base font-bold text-gray-100 font-mono mt-0.5">{sortedLeaderboard.length} Models</div>
          </div>
          <Layers className="w-6 h-6 text-amber-400 opacity-80" />
        </div>

        <div className="bg-[#1a1d29] border border-gray-800 p-4 rounded-xl flex items-center justify-between shadow-md">
          <div>
            <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Universe</div>
            <div className="text-base font-bold text-blue-400 font-mono mt-0.5">{universe}</div>
          </div>
          <Trophy className="w-6 h-6 text-emerald-400 opacity-80" />
        </div>
      </div>

      {/* Full Leaderboard Table (with 1D, 1W, 4W & sorting) */}
      <div className="bg-[#1a1d29] border border-gray-800 rounded-xl p-4 shadow-lg flex flex-col relative z-10">
        <div className="flex items-center justify-between mb-3 px-1">
          <h3 className="text-sm font-bold text-gray-100 uppercase tracking-wider flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-400" />
            Strategy Leaderboard ({universe})
          </h3>
          <span className="text-xs text-gray-400 font-mono flex items-center gap-1">
            <ArrowUpDown className="w-3 h-3 text-blue-400" />
            Click headers to sort
          </span>
        </div>
        <div className="overflow-x-auto overflow-y-auto max-h-[360px] border border-gray-800/80 rounded-lg">
          <table className="w-full text-left text-xs font-mono border-collapse">
            <thead className="sticky top-0 bg-[#14171f] text-gray-400 uppercase text-[11px] border-b border-gray-800 z-10 select-none">
              <tr>
                <th className="py-3 px-4">#</th>
                <th className="py-3 px-4">Strategy Variant</th>
                <th
                  onClick={() => toggleSort("total_return_pct")}
                  className="py-3 px-4 text-right cursor-pointer hover:text-blue-400 transition-colors"
                >
                  Return {sortKey === "total_return_pct" ? (sortDir === "desc" ? "↓" : "↑") : ""}
                </th>
                <th
                  onClick={() => toggleSort("chg_1d")}
                  className="py-3 px-4 text-right cursor-pointer hover:text-blue-400 transition-colors"
                >
                  1D {sortKey === "chg_1d" ? (sortDir === "desc" ? "↓" : "↑") : ""}
                </th>
                <th
                  onClick={() => toggleSort("chg_1w")}
                  className="py-3 px-4 text-right cursor-pointer hover:text-blue-400 transition-colors"
                >
                  1W {sortKey === "chg_1w" ? (sortDir === "desc" ? "↓" : "↑") : ""}
                </th>
                <th
                  onClick={() => toggleSort("chg_4w")}
                  className="py-3 px-4 text-right cursor-pointer hover:text-blue-400 transition-colors"
                >
                  4W {sortKey === "chg_4w" ? (sortDir === "desc" ? "↓" : "↑") : ""}
                </th>
                <th
                  onClick={() => toggleSort("max_dd_pct")}
                  className="py-3 px-4 text-right cursor-pointer hover:text-blue-400 transition-colors"
                >
                  Max DD {sortKey === "max_dd_pct" ? (sortDir === "desc" ? "↓" : "↑") : ""}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50 text-gray-200">
              {sortedLeaderboard.map((row, idx) => {
                const isLive = row.key === "live_portfolio";
                return (
                  <tr key={row.key} className={`hover:bg-gray-800/40 transition-colors ${isLive ? "bg-cyan-950/30" : ""}`}>
                    <td className="py-2.5 px-4 font-bold text-gray-500">{idx + 1}</td>
                    <td className="py-2.5 px-4 font-semibold">
                      <span className={isLive ? "text-cyan-300 font-bold" : "text-gray-100"}>{row.key}</span>
                      {isLive && (
                        <span className="ml-2 text-[9px] uppercase tracking-wider font-bold text-cyan-300 border border-cyan-800 rounded px-1.5 py-0.5 bg-cyan-950">
                          LIVE
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-4 text-right font-bold">{pctCell(row.total_return_pct)}</td>
                    <td className="py-2.5 px-4 text-right font-mono">{pctCell(row.chg_1d)}</td>
                    <td className="py-2.5 px-4 text-right font-mono">{pctCell(row.chg_1w)}</td>
                    <td className="py-2.5 px-4 text-right font-mono">{pctCell(row.chg_4w)}</td>
                    <td className="py-2.5 px-4 text-right font-bold text-rose-400">
                      {row.max_dd_pct != null ? `${row.max_dd_pct.toFixed(2)}%` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Equity Curves Chart */}
      <div className="bg-[#1a1d29] border border-gray-800 rounded-xl p-5 shadow-lg flex flex-col gap-4 relative z-30">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-bold text-gray-100 uppercase tracking-wider">Equity Curves (Rebased to 1000)</h3>
            <p className="text-xs text-gray-400">All model series normalized to start at 1000 for direct visual comparison</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedCurves(new Set(Object.keys(data?.equity_curves || {})))}
              className="px-2.5 py-1 text-[11px] font-semibold bg-[#14171f] hover:bg-gray-800 text-gray-300 border border-gray-700 rounded-md transition-colors"
            >
              Select All
            </button>
            <button
              onClick={() => setSelectedCurves(new Set())}
              className="px-2.5 py-1 text-[11px] font-semibold bg-[#14171f] hover:bg-gray-800 text-gray-300 border border-gray-700 rounded-md transition-colors"
            >
              Clear
            </button>
          </div>
        </div>

        {/* Strategy Selector Pills */}
        <div className="flex flex-wrap gap-1.5 max-h-[110px] overflow-y-auto p-2.5 bg-[#0f1116] border border-gray-800 rounded-lg">
          {Object.keys(data?.equity_curves || {}).map((key, idx) => {
            const isSelected = selectedCurves?.has(key);
            const isLive = key === "live_portfolio";
            const color = isLive ? "#06b6d4" : PALETTE[idx % PALETTE.length];
            return (
              <button
                key={key}
                onClick={() => {
                  const next = new Set(selectedCurves);
                  if (next.has(key)) next.delete(key);
                  else next.add(key);
                  setSelectedCurves(next);
                }}
                className={`px-2.5 py-1 text-xs font-mono rounded-md border transition-all ${
                  isSelected
                    ? isLive
                      ? "bg-cyan-950/60 text-cyan-300 font-bold border-cyan-500 shadow-sm"
                      : "bg-gray-800 text-white font-bold border-gray-600 shadow-sm"
                    : "bg-[#14171f]/60 text-gray-500 border-gray-800 hover:text-gray-300"
                }`}
                style={isSelected ? { borderLeftColor: color, borderLeftWidth: "4px" } : {}}
              >
                {key}
              </button>
            );
          })}
        </div>

        {/* Chart Box */}
        <div className="h-[380px] w-full pt-2 relative z-30">
          {chartData.length === 0 ? (
            <div className="flex items-center justify-center h-full text-xs font-mono text-gray-500">
              No series selected to plot.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 15, left: -5, bottom: 0 }}>
                <CartesianGrid stroke="#1f2937" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="date" stroke="#6b7280" tick={{ fontSize: 11, fontFamily: 'monospace' }} axisLine={{ stroke: '#374151' }} tickLine={false} />
                <YAxis stroke="#6b7280" tick={{ fontSize: 11, fontFamily: 'monospace' }} domain={["auto", "auto"]} orientation="right" axisLine={{ stroke: '#374151' }} tickLine={false} />
                <Tooltip
                  content={<EquityTooltip />}
                  wrapperStyle={{ zIndex: 1000, pointerEvents: 'none' }}
                  cursor={{ stroke: '#4b5563', strokeWidth: 1, strokeDasharray: '3 3' }}
                />
                {activeCurveKeys.map((key, idx) => {
                  const isLive = key === "live_portfolio";
                  const color = isLive ? "#06b6d4" : PALETTE[idx % PALETTE.length];
                  return (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      stroke={color}
                      dot={false}
                      strokeWidth={isLive ? 2.5 : 1.75}
                      connectNulls={true}
                      isAnimationActive={false}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Monthly Performance & Correlation Matrix */}
      <div className="flex flex-col gap-6 pb-6 relative z-10">
        {/* Monthly Returns */}
        <div className="bg-[#1a1d29] border border-gray-800 rounded-xl p-4 shadow-lg flex flex-col">
          <h3 className="text-sm font-bold text-gray-100 uppercase tracking-wider mb-3">Monthly Return Breakdown (%)</h3>
          <div className="overflow-x-auto overflow-y-auto max-h-[380px] border border-gray-800 rounded-lg bg-[#14171f]">
            <table className="w-full text-left text-xs font-mono border-collapse">
              <thead className="sticky top-0 bg-[#1a1d29] text-gray-400 uppercase text-[11px] border-b border-gray-800 z-10">
                <tr>
                  <th className="py-3 px-3">Variant</th>
                  {months.map((m) => (
                    <th key={m} className="py-3 px-3 text-right">{monthLabel(m)}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {Object.entries(monthlyReturns).map(([strat, row]) => (
                  <tr key={strat} className="hover:bg-gray-800/40">
                    <td className="py-2.5 px-3 font-semibold text-gray-200">{strat}</td>
                    {months.map((m) => {
                      const v = row[m];
                      return (
                        <td key={m} className={`py-2.5 px-3 text-right font-bold ${cellBgColor(v)}`}>
                          {v != null ? `${v >= 0 ? "+" : ""}${v.toFixed(1)}%` : "—"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Correlation Heatmap */}
        <div className="bg-[#1a1d29] border border-gray-800 rounded-xl p-4 shadow-lg flex flex-col">
          <h3 className="text-sm font-bold text-gray-100 uppercase tracking-wider mb-3">Model Correlation Matrix</h3>
          <div className="overflow-x-auto overflow-y-auto max-h-[380px] border border-gray-800 rounded-lg bg-[#14171f] p-2">
            {(() => {
              const matrix = data?.correlation?.matrix || (data?.correlation && Object.keys(data.correlation).length > 0 ? data.correlation : null);
              if (!matrix) return <div className="p-6 text-center text-gray-500 font-mono text-xs">No correlation matrix available.</div>;
              return (
                <table className="w-full text-center text-xs font-mono border-collapse">
                  <thead>
                    <tr>
                      <th className="p-2 text-left text-gray-500 font-sans font-bold">Variant</th>
                      {Object.keys(matrix).map((k) => (
                        <th key={k} className="p-2 text-gray-400 font-normal">{k}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(matrix).map(([rKey, row]) => (
                      <tr key={rKey} className="border-t border-gray-800/40">
                        <td className="p-2 text-left font-bold text-gray-300">{rKey}</td>
                        {Object.values(row || {}).map((val, cIdx) => (
                          <td key={cIdx} className={`p-2 rounded ${val >= 0.7 ? "bg-blue-500/20 text-blue-300 font-bold" : "text-gray-400"}`}>
                            {val != null ? val.toFixed(2) : "—"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}
